import { Form, Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';

import { useParams } from 'react-router-dom';

import { CSSObjectWithLabel } from 'react-select';

import AgGridSelectableFilter
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/AgGridSelectableFilter';

import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { Input } from '@/components/common/InputTag4/InputTag4';
import styles from './billing-cadence-modal.module.scss';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import { getBillingCyclesColumns } from '../columns/billingCyclesColumns';
import useGetBillingCycles from '../hooks/useGetBillingCycles';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import Spinner from '@/components/common/Spinner/Spinner';
import { useSelectedLines } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';
import ModalFormDatepicker from '@/components/business/ModalForm/ModalFormDatepicker/ModalFormDatepicker';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { isValidDate } from '@/utils/date.utils';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { DATES_MODIFIED } from '@/config/reasonTypes.config';
import {
  BillingCadenceFormFields,
  billingCadenceValidationSchema,
} from './billingCadenceValidationSchema';
import useUpdateBillingCadence from '../hooks/useUpdateBillingCadence';
import useGetContractLinesFromCache from '@/hooks/contract/useGetContractLinesFromCache.hook';
import { ContractBillingStatusPascalCaseEnum } from '@/graphql/contracts/_generated_/contracts';

interface IBillingCadenceProps {
  onClose: () => void;
  isReadOnly?: boolean;
}

export default function BillingCadenceModal({ onClose, isReadOnly = false }: IBillingCadenceProps) {
  const [cyclesPerLine, setCyclesPerLine] = useState<string>('3');
  const [showPauseWarningModal, setShowPauseWarningModal] = useState(false);
  const pendingFormValuesRef = useRef<BillingCadenceFormFields | null>(null);
  const { getBillingCycles, billingCycles, isLoadingBillingCycles } =
    useGetBillingCycles();
  const rowData = useMemo(() => {
    const groupedByLineId = billingCycles.reduce<
    Record<string, typeof billingCycles>
    >((storage, billingCycle) => {
      const key = billingCycle!.contractLineID;
      if (!storage[key]) {
        storage[key] = [];
      }
      storage[key].push(billingCycle);

      return storage;
    }, {});

    const rows = [];
    for (const groupKey in groupedByLineId) {
      let lines = groupedByLineId[groupKey];
      lines = lines.sort(
        (a: (typeof billingCycles)[0], b: (typeof billingCycles)[0]) =>
          new Date(b!.startDate).getTime() -
          new Date(a!.startDate).getTime()
      );

      const cyclesToTake = cyclesPerLine === 'All' ? lines.length : parseInt(cyclesPerLine, 10);
      rows.push(...lines.slice(0, cyclesToTake));
    }

    return rows;
  }, [billingCycles, cyclesPerLine]);

  const gridRef = useRef<AgGridReact>(null);
  const columns = getBillingCyclesColumns();
  const { contractId }: { contractId: UUID } = useParams();
  const { contract, isContractLoading } = useGetContractDetails(contractId);
  const selectedLineRows = useSelectedLines();
  const { reasonCodes, isLoading, getReasonCodes } = useGetReasonCodes();
  const { updateBillingCadence, isLoadingUpdateBillingCadence } =
    useUpdateBillingCadence(contractId);
  const { cachedBCContractLines, cachedVariableContractLines } = useGetContractLinesFromCache(contractId);

  const isDataLoading = useMemo(() => {
    return isContractLoading || isLoadingBillingCycles;
  }, [isContractLoading, isLoadingBillingCycles]);

  const hasSelectedLinesPaused = useMemo(() => {
    if (selectedLineRows.length === 0) return false;

    const bcLines = cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items || [];
    const variableLines = cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items || [];

    const selectedLineIds = selectedLineRows.map(line => line.id);

    const selectedBcLines = bcLines.filter(line => line?.id && selectedLineIds.includes(line.id));
    const selectedVariableLines = variableLines.filter(line => line?.iD && selectedLineIds.includes(line.iD));

    const bcLinesPaused = selectedBcLines.some(line =>
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    const variableLinesPaused = selectedVariableLines.some(line =>
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    return bcLinesPaused || variableLinesPaused;
  }, [selectedLineRows, cachedBCContractLines, cachedVariableContractLines]);

  const hasPauseActive = hasSelectedLinesPaused;

  const executeUpdate = (values: BillingCadenceFormFields, resetForm: () => void) => {
    if (!isLoadingUpdateBillingCadence) {
      updateBillingCadence({
        contractId,
        contractLineIds: selectedLineRows.map((cl) => cl.id),
        billingCycleStartDate: values.billingCycleStartDate,
        changeReason: {
          name: values.changeReasonName,
          comment: values.changeReasonComment,
        },
      });
      resetForm();
    }
  };

  useEffect(() => {
    if (selectedLineRows.length > 0) {
      getBillingCycles({
        filter: {
          contractLineID: {
            containsValues: selectedLineRows.map((cl) => cl.id),
          },
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLineRows]);

  useEffect(() => {
    getReasonCodes(DATES_MODIFIED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cyclesPerLineOptions = useMemo(() => {
    return ['3', '5', '10', 'All'].map((value) => ({
      value: value,
      label: value,
    }));
  }, []);

  return (
    <AgModalWindow
      title={'Update/Review Billing Cycles'}
      isOpen={true}
      onClose={onClose}
      contentStyle={{ width: '90vw' }}
    >
      {isDataLoading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: 200 }}
        >
          <Spinner />
        </div>
      )}
      {!isDataLoading && (
        <>
          <AgConfirmationModal
            isOpen={showPauseWarningModal}
            title="Pause Warning"
            onClose={() => {
              setShowPauseWarningModal(false);
              pendingFormValuesRef.current = null;
            }}
            onConfirm={() => {
              if (pendingFormValuesRef.current) {
                executeUpdate(pendingFormValuesRef.current, () => { });
                pendingFormValuesRef.current = null;
              }
              setShowPauseWarningModal(false);
            }}
            contentStyle={{ width: 500 }}
          >
            <strong>Warning:</strong> This action will change the billing date.
            A Pause to billing is set. Please review your Pause End Date to ensure accurate billing.
            <br /><br />
            Do you want to continue?
          </AgConfirmationModal>
          {!isReadOnly && <Formik<BillingCadenceFormFields>
            initialValues={{
              billingCycleStartDate: undefined,
              changeReasonName: '',
              changeReasonComment: '',
            }}
            validationSchema={billingCadenceValidationSchema}
            onSubmit={(values, { resetForm }) => {
              if (hasPauseActive) {
                pendingFormValuesRef.current = values;
                setShowPauseWarningModal(true);
              } else {
                executeUpdate(values, resetForm);
              }
            }}
            validateOnMount={true}
          >
            {({
              isValid,
              setFieldTouched,
              setFieldValue,
              errors,
              values,
              touched,
            }) => (
              <Form>
                <div className={styles['billing-cadence-container']}>
                  <div className={styles['billing-cadence-main']}>
                    <div className={styles['billing-cadence-main-row']}>
                      <div className={styles['row-field']}>
                        <label className={styles.label}>Customer Number</label>
                        <Input
                          disabled={true}
                          value={contract?.customer.customerAccountNumber}
                        />
                      </div>

                      <div className={styles['row-field']}>
                        <label className={styles.label}>Contract Number</label>
                        <Input
                          disabled={true}
                          value={contract?.contractNumber}
                        />
                      </div>
                    </div>
                    <div
                      className={`${styles['billing-cadence-main-row']} mt-2`}
                    >
                      <ModalFormDatepicker
                        id="billingCycleStartDate"
                        labelTitle="New BC Start Date"
                        requiredIcon={true}
                        className={styles['row-field']}
                        onChange={({ value }) => {
                          setFieldTouched('billingCycleStartDate', true);
                          setFieldValue('billingCycleStartDate', value);
                        }}
                        value={
                          isValidDate(values.billingCycleStartDate)
                            ? new Date(values.billingCycleStartDate)
                            : undefined
                        }
                        validationMessage={
                          errors?.billingCycleStartDate && touched.billingCycleStartDate
                            ? errors.billingCycleStartDate as string
                            : undefined
                        }
                      />

                      <ModalFormTextInput
                        disabled={true}
                        value="Dates Modified"
                        labelTitle="Reason Type"
                        id="reasonType"
                        className={styles['row-field']}
                      />

                      <ModalFormDropdown<ReasonCode>
                        id="changeReasonName"
                        className={styles['row-field']}
                        labelTitle="Reason Name"
                        requiredIcon={true}
                        isSearchable={true}
                        optionsData={reasonCodes}
                        selectedOption={undefined}
                        placeholder="Select or type to search"
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.code}
                        menuPortalTarget={document.querySelector('body')}
                        customStyles={{
                          menuList: (provided) =>
                            ({
                              ...provided,
                              maxHeight: 200,
                            }) as CSSObjectWithLabel,
                        }}
                        handleChange={(newValue) => {
                          setFieldTouched('changeReasonName', true, false);
                          setFieldValue('changeReasonName', newValue?.code);
                        }}
                        validationMessage={
                          errors?.changeReasonName && touched.changeReasonName
                            ? errors.changeReasonName
                            : undefined
                        }
                        isLoading={isLoading}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className={styles['billing-cadence-comment']}>
                    <ModalFormTextarea
                      id="changeReasonComment"
                      className={styles['billing-cadence-comment-field']}
                      labelTitle="Comment"
                      requiredIcon={true}
                      placeholder="Enter short comments"
                      value={values.changeReasonComment}
                      onChange={(event) => {
                        setFieldTouched('changeReasonComment', true, false);
                        setFieldValue(
                          'changeReasonComment',
                          FormattingUtils.replaceControlCharacters(
                            event.target.value
                          )
                            ? event.target.value
                            : undefined
                        );
                      }}
                      validationMessage={
                        errors?.changeReasonComment &&
                          touched.changeReasonComment
                          ? errors.changeReasonComment
                          : undefined
                      }
                      isFloatingValidation={true}
                    />
                  </div>
                </div>
                <div className={styles.button}>
                  <Button
                    className={styles['button-save']}
                    disabled={!isValid}
                    buttonStyleType={ButtonStyleType.PRIMARY}
                    type="submit"
                  >
                    Save
                  </Button>
                </div>
              </Form>
            )}
          </Formik>}

          <div className={styles['cycles-per-line']}>
            <div className={styles['cycles-per-line-inner']}>
              <label className={styles['cycles-per-line-label']}>
                Cycles per line:
              </label>
              <select
                value={cyclesPerLine}
                onChange={(e) => setCyclesPerLine(e.target.value)}
                className={styles['cycles-per-line-select']}
              >
                {cyclesPerLineOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ height: `${rowData.length > 4 ? 400 : 250}px` }}>
            <TableGrid<any>
              ref={gridRef}
              domLayout="normal"
              getRowId={(params) => params.data.billingCycleID}
              suppressRowClickSelection={true}
              rowData={rowData}
              columnDefs={columns}
              rowSelection="multiple"
              pagination={false}
              suppressPaginationPanel={true}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                suppressSizeToFit: false
              }}
              components={{
                SelectableFilter: AgGridSelectableFilter,
              }}
              enableRangeSelection={true}
              allowContextMenuWithControlKey={true}
              alwaysMultiSort={true}
              headerHeight={40}
              rowHeight={45}
              context={{}}
            />
          </div>

          <ActionButtonsWrapper
            leftButton={
              <Button
                buttonStyleType={ButtonStyleType.SECONDARY}
                onClick={onClose}
              >
                Cancel
              </Button>
            }
          />
        </>
      )}
    </AgModalWindow>
  );
}
